import { warn } from '@4players/odin-common';

export interface RoomPresenceLock {
  release(): void;
}

// Two tabs in the same browser share the same andorUUID. Calling this before
// joining a room reserves the (userId, roomId) pair for this tab and returns
// `null` if another tab already holds it — preventing both tabs from joining
// as the same userId and racing per-user state.
//
// The browser auto-releases the lock on tab close or crash, so no unload
// handler is needed. When `navigator.locks` is unavailable, returns a no-op
// handle so callers can proceed (graceful degradation).
export function acquireRoomPresenceLock(
  userId: string,
  roomId: string
): Promise<RoomPresenceLock | null> {
  if (!('locks' in navigator)) {
    warn('navigator.locks unavailable; skipping duplicate-tab room-presence check');
    return Promise.resolve({ release: () => {} });
  }
  const lockName = `odin-room-presence:${userId}:${roomId}`;
  return new Promise<RoomPresenceLock | null>((resolveAcquire) => {
    navigator.locks
      .request(lockName, { ifAvailable: true }, (lock) => {
        if (!lock) {
          resolveAcquire(null);
          return Promise.resolve();
        }
        return new Promise<void>((resolveHold) => {
          resolveAcquire({ release: () => resolveHold() });
        });
      })
      .catch((e) => {
        warn('room-presence lock request failed; allowing join', { e });
        resolveAcquire({ release: () => {} });
      });
  });
}
